import { blue, orange } from '@mui/material/colors';
import { faIR } from '@mui/material/locale';
import { alpha, createTheme } from '@mui/material/styles';

export const emotionLightTheme = {
  colors: {
    brand: {
      green: {
        primary: '#009300',
      },
      grey: {
        primary: '#434343',
        secondary: '#505050',
        ternary: '#9EADB4',
        quaternary: '#D3D8DB',
      },
      orange: {
        primary: '#EC6608',
        secondary: '#F36F21',
        hover: '#db610b',
      },
      blue: {
        primary: '#27348B',
        secondary: '#268AD6',
        ternary: '#0B7AD1',
        quaternary: '#004274',
        hover: '#1d2978',
      },
    },
    layout: {
      main: '#fff',
      reverse: '#000000',
      disabled: '#E4E4E4',
      dark: '#082031',
      darker: '#051E30',
      light: '#0065B3',
      transparentLight: 'rgba(38, 138, 214, .15)',
      borderWhite: 'rgba(255, 255, 255, .07)',
      borderBlue: '#1F4864',
    },
  },
};

export const MUILightTheme = createTheme({
  direction: 'rtl',
  palette: {
    brand: {
      green: { ...emotionLightTheme.colors.brand.green },
      grey: { ...emotionLightTheme.colors.brand.grey },
      orange: { ...emotionLightTheme.colors.brand.orange },
      blue: { ...emotionLightTheme.colors.brand.blue },
    },
    layout: { ...emotionLightTheme.colors.layout },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        virtualScroller: {
          marginTop: '56px !important',
        },
        root: {
          'border': 'none!important',
          '& .MuiDataGrid-columnHeaders': {
            'border': '1px solid',
            'borderColor': 'rgba(100,100,100,.1) !important',
            'backgroundColor': 'whitesmoke',
            'height': '56px !important',
            'minHeight': 'unset !important',
            'direction': 'rtl',
            'color': '#51575c',
            '& .MuiSvgIcon-root': {
              color: '#51575c',
            },
            '& .MuiDataGrid-columnHeader': {
              height: '53px !important',
            },
          },
          '& .MuiDataGrid-toolbarContainer': {
            '& .MuiSvgIcon-root': {
              marginLeft: '.5rem',
            },
          },
          '& .MuiDataGrid-iconSeparator': {
            fill: 'rgba(100,100,100,.2) !important',
          },
          '& .MuiDataGrid-cell': {
            borderBottomColor: 'rgba(100,100,100,.2) !important',
          },
          '& .MuiDataGrid-columnHeaderRow': {
            border: 'none',
          },
          '& .MuiDataGrid-columnsContainer': {
            direction: 'ltr !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            direction: 'rtl !important',
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        sx: {
          transformOrigin: 'right !important',
          left: 'inherit !important',
          right: '1.75rem !important',
          fontSize: 'normal',
          fontWeight: 400,
          overflow: 'unset',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingRight: '13px !important',
          paddingLeft: '20px !important',
        },
        icon: {
          left: '7px',
          right: 'unset',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: emotionLightTheme.colors.layout.disabled,
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        sx: {
          '& legend': {
            textAlign: 'right',
            fontSize: 'normal',
          },
        },
      },
    },
    MuiTextField: {
      variants: [
        {
          props: { variant: 'outlinedBlueBrand' },
          style: {
            '&:focus': {
              borderColor: emotionLightTheme.colors.brand.blue.primary,
            },
          },
        },
      ],
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'containedBlueBrand' },
          style: {
            'backgroundColor': emotionLightTheme.colors.brand.blue.primary,
            'color': emotionLightTheme.colors.brand.orange.primary,
            '&:hover': {
              backgroundColor: emotionLightTheme.colors.brand.blue.hover,
            },
            '.MuiTouchRipple-child': {
              color: blue[700],
            },
            '&.MuiLoadingButton-loading': {
              backgroundColor: alpha(
                emotionLightTheme.colors.brand.blue.primary,
                0.4,
              ),
            },
            '.MuiLoadingButton-loadingIndicator': {
              color: emotionLightTheme.colors.brand.orange.primary,
            },
          },
        },
        {
          props: { variant: 'containedBlueBrand', size: 'large' },
          style: {
            'backgroundColor': emotionLightTheme.colors.brand.blue.primary,
            'color': emotionLightTheme.colors.brand.orange.primary,
            '&:hover': {
              backgroundColor: emotionLightTheme.colors.brand.blue.hover,
            },
            '.MuiTouchRipple-child': {
              color: blue[700],
            },
            '&.MuiLoadingButton-loading': {
              backgroundColor: alpha(
                emotionLightTheme.colors.brand.blue.primary,
                0.4,
              ),
            },
            '.MuiLoadingButton-loadingIndicator': {
              color: emotionLightTheme.colors.brand.orange.primary,
            },
            'fontSize': '1.015rem',
            'fontWeight': 'bold',
          },
        },
        {
          props: { variant: 'containedOrangeBrand' },
          style: {
            'backgroundColor': emotionLightTheme.colors.brand.orange.primary,
            'color': emotionLightTheme.colors.layout.main,
            '&:hover': {
              backgroundColor: emotionLightTheme.colors.brand.orange.hover,
            },
            '.MuiTouchRipple-child': {
              color: orange[700],
            },
            '&.MuiLoadingButton-loading': {
              backgroundColor: alpha(
                emotionLightTheme.colors.brand.orange.primary,
                0.4,
              ),
            },
            '.MuiLoadingButton-loadingIndicator': {
              color: emotionLightTheme.colors.brand.blue.primary,
            },
          },
        },
        {
          props: { variant: 'containedOrangeBrand', size: 'large' },
          style: {
            'backgroundColor': emotionLightTheme.colors.brand.orange.primary,
            'color': emotionLightTheme.colors.layout.main,
            '&:hover': {
              backgroundColor: emotionLightTheme.colors.brand.orange.hover,
            },
            '.MuiTouchRipple-child': {
              color: orange[700],
            },
            '&.MuiLoadingButton-loading': {
              backgroundColor: alpha(
                emotionLightTheme.colors.brand.orange.primary,
                0.4,
              ),
            },
            '.MuiLoadingButton-loadingIndicator': {
              color: emotionLightTheme.colors.brand.blue.primary,
            },
            'fontSize': '1.015rem',
            'fontWeight': 'bold',
          },
        },
      ],
    },
  },
  typography: {
    fontFamily: '"Yekan-Medium", "Roboto", "Helvetica", "Arial", sans-serif',
  },

  faIR,
});
